import type { ServerDoc } from './api'
import type { LocalDoc } from './db'

/**
 * Zusammenführen von lokaler und entfernter Fassung eines Dokuments.
 *
 * Die Grundregel ist bewusst simpel: die jüngere Änderung gewinnt das ganze
 * Dokument. Ein Roadbook wird von wenigen Geräten derselben Person gepflegt,
 * meist nacheinander; ein feingranulares Verschmelzen einzelner Felder wäre
 * mehr Angriffsfläche für Fehler als Gewinn.
 *
 * Vor dieser Regel stehen zwei Ausnahmen, ohne die sie Daten vernichtet:
 * die unangetastete Startbestückung darf kein Dokument mit Vorgeschichte
 * überschreiben, und die Antwort des Servers auf den eigenen Push ist keine
 * fremde Änderung.
 */
export function mergeRemote(local: LocalDoc | undefined, remote: ServerDoc): LocalDoc {
  const fromRemote: LocalDoc = {
    kind: remote.kind,
    id: remote.id,
    body: remote.body,
    rev: remote.rev,
    deleted: remote.deleted,
    dirty: 0,
    updatedAt: remote.updatedAt,
    // Die Änderungsnummer bleibt auch dann stehen, wenn der Inhalt des Servers
    // gewinnt. Sie beantwortet nur eine Frage: welche lokale Schreibgeneration
    // steckt in diesem Eintrag. Wird sie hier fallengelassen, hält
    // mergeSnapshot die verworfene Fassung im React-Zustand für einen noch
    // unterwegs befindlichen Schreibvorgang und stellt sie wieder her.
    localSeq: local?.localSeq,
  }

  // Nichts Lokales oder nichts Ungesichertes: der Server hat recht.
  if (!local || local.dirty === 0) return fromRemote

  // Lokal liegt nur die unangetastete Startbestückung, entfernt ein Dokument
  // mit Vorgeschichte. Auf einem zweiten Gerät ist die Vorlage frisch angelegt
  // und damit nach Zeitstempel immer "jünger" als die echte, längst geplante
  // Reise. Ohne diese Regel würde die Vorlage die Reise überschreiben, und
  // zwar auf allen Geräten.
  //
  // Die Markierung kommt aus RoadbookProvider und fällt bei der ersten echten
  // Änderung weg. Früher stand hier "rev 0", also "war noch nie beim Server":
  // das traf auch jeden offline geschriebenen Tagebucheintrag, dessen stageId
  // der Server längst kennt, und löschte ihn beim ersten Abgleich still weg.
  if (local.seed === 1 && remote.rev > 0) return fromRemote

  // Das ist die Antwort auf den eigenen Push: die Revision des Servers ist
  // bereits vermerkt, die lokale Änderung entstand danach. Wer hier nach
  // Zeitstempel entscheidet, verwirft alles, was während des Pushs getippt
  // wurde, denn der Server stempelt beim Anwenden neu.
  if (local.rev === remote.rev) return local

  // Die lokale Revision liegt über der des Servers. Das gibt es regulär nicht,
  // wohl aber nach einem Rücksetzen des Servers aus einer Sicherung. Bliebe die
  // lokale Basis stehen, meldete jeder Push denselben Konflikt, im Minutentakt
  // und für immer. Also den Inhalt behalten, aber auf der Basis des Servers
  // neu aufsetzen.
  if (local.rev > remote.rev) return { ...local, rev: remote.rev, dirty: 1 }

  // Der Server ist neuer als die lokale Änderung: die lokale Änderung ist
  // überholt und wird verworfen.
  if (remote.updatedAt >= local.updatedAt) return fromRemote

  // Die lokale Änderung gewinnt, übernimmt aber die Revision des Servers.
  // Nur so setzt der nächste Push auf der richtigen Basis auf und der Konflikt
  // löst sich, statt sich zu wiederholen.
  return { ...local, rev: remote.rev, dirty: 1 }
}

/**
 * Führt einen frisch gelesenen Schnappschuss der Datenbank mit dem
 * React-Zustand zusammen.
 *
 * Der Schnappschuss ersetzt den Zustand nicht blind: die Datenbank wird
 * nebenher beschrieben, ein vom Abgleich ausgelöstes Reload liest also unter
 * Umständen einen Stand, in dem der gerade getippte Buchstabe noch fehlt. Ihn
 * zu übernehmen wäre schlimmer als ein veralteter Schnappschuss, denn die
 * Eingabefelder hängen am React-Zustand: der Buchstabe verschwände unter dem
 * Cursor, und der nächste Tastendruck schriebe den zurückgesetzten Text auch
 * noch in die Datenbank.
 *
 * Vorrang bekommt der Zustand deshalb genau dann, wenn er eine lokale
 * Schreibgeneration trägt, die die Datenbank noch nicht kennt: localSeq ist
 * vorhanden und grösser als die gespeicherte. Nur write() und deletePhoto
 * vergeben eine solche Nummer, und beide setzen ihren Schreibvorgang unmittelbar
 * danach ab - genau dieses Fenster ist gemeint.
 *
 * Früher entschied hier der Zeitstempel, und das war der Rückfall in das
 * Datenverlust-Szenario, das die Markierung seed in mergeRemote schliesst: die
 * beim Erststart angelegte Vorlage ist per Konstruktion jünger als die echte,
 * längst geplante Reise. Der Abgleich schrieb die Reise korrekt in die
 * Datenbank, und dieses Reload holte die Vorlage sofort wieder nach vorn - für
 * immer, denn jede weitere Runde tat dasselbe.
 *
 * Die Bindung an localSeq ist der Markierung seed vorzuziehen, weil sie nicht
 * auf eine einzelne Dokumentart zeigt, sondern auf den einzigen Grund, aus dem
 * der Zustand überhaupt vorne liegen darf. Alles, was nicht aus einem lokalen
 * Schreibvorgang stammt - Vorlage, Import, Serverfassung und was später noch
 * dazukommt - hat keine Nummer und damit auch keinen Vorrang.
 *
 * @param current der bisherige React-Zustand.
 * @param snapshot der Bestand, wie ihn die Datenbank gerade liefert.
 */
export function mergeSnapshot(
  current: ReadonlyMap<string, LocalDoc>,
  snapshot: ReadonlyMap<string, LocalDoc>,
): Map<string, LocalDoc> {
  const next = new Map(snapshot)
  for (const [key, doc] of current) {
    // Kein lokaler Schreibvorgang: der Eintrag kommt ohnehin aus der Datenbank.
    if (doc.localSeq === undefined) continue
    const stored = next.get(key)
    // Die Datenbank ist gleich weit oder weiter: ihr Stand ist der bessere, er
    // hat den Abgleich gesehen.
    if (stored !== undefined && stored.localSeq !== undefined && stored.localSeq >= doc.localSeq) {
      continue
    }
    next.set(key, doc)
  }
  return next
}
